// Share to Discord (Oct 13, 2026): what a report or a troop movement says when it's posted into the guild's Discord by the
// Valor Tools bot (worker/discord.ts → Valor Tools src/vweb.js). Plain fields only: Valor Tools escapes them and turns the
// times into Discord timestamps (each reader sees their own time) with the UTC time beside them.
import type { ReportBody } from "@/api/types";
import { TROOP_KEYS, TROOP_NAMES, type TroopKey } from "./constants";
import { fmtCoords, fmtNum } from "./format";
import { parseGameTime } from "./time";
import type { MoveEntry } from "./city";
import { paceLabel } from "./city";
import {
  SEAL_WORD, haulInfo, isAttackerSide, isBarbarian, num, parties, recipientWon, reportType, resourceWords, sealOf, sumUnits, type Party,
} from "./reports";

export type ShareKind = "report" | "outgoing" | "incoming";
export type ShareTone = "attack" | "incoming" | "support" | "returning" | "report" | "scout";
export interface ShareCard {
  kind: ShareKind;
  tone: ShareTone;
  title: string;
  times: { label: string; ms: number }[];
  fields: { name: string; value: string; inline?: boolean }[];
  troops: { name: string; n: number | null; lost?: number }[];
  troopsLabel?: string;
  note?: string;
  /** a report's public link (/r/<id>, Oct 15 2026): Valor Tools puts it on the card's title and an "Open full report" button */
  url?: string;
}
/** The channels a guild offers (Valor Tools: ops, intel, support, officers). */
export type ShareChannel = "ops" | "intel" | "support" | "officers";

const troopList = (units: Partial<Record<TroopKey | string, number | string>> | undefined, lost?: Partial<Record<TroopKey | string, number | string>>) =>
  TROOP_KEYS.filter((k) => num(units?.[k]) > 0 || num(lost?.[k]) > 0).map((k) => ({ name: TROOP_NAMES[k], n: num(units?.[k]), ...(lost && num(lost[k]) > 0 ? { lost: num(lost[k]) } : {}) }));

/** A troop movement from the Rally Point (outgoing or incoming). `home`: the city it's in, as "x,y" coordinates. */
export function movementCard(e: MoveEntry, home: [number, number] | null): ShareCard {
  const out = e.dir === "out";
  const ret = e.kind.startsWith("return");
  const verb = out
    ? e.kind === "attack" ? "Attacking" : e.kind === "support" ? "Supporting" : "Returning from"
    : e.kind === "attack" ? "Incoming attack from" : e.kind === "support" ? "Incoming support from" : "Returning from";
  const tone: ShareTone = ret ? "returning" : e.kind === "support" ? "support" : out ? "attack" : "incoming";
  const pace = paceLabel(e, home);
  const fields: ShareCard["fields"] = [
    { name: "From", value: `${e.m.origin_name} ${fmtCoords(e.m.origin_coords)}`, inline: true },
    { name: "To", value: `${e.m.target_name} ${fmtCoords(e.m.target_coords)}`, inline: true },
  ];
  if (pace) fields.push({ name: "Pace", value: pace + (!out && e.kind === "attack" && /Scholar/.test(pace) ? " · loyalty at risk" : ""), inline: true });
  if (e.kind === "attack" && Number.isFinite(Number(e.m.morale))) fields.push({ name: "Morale", value: `${Math.round(Number(e.m.morale))} %`, inline: true });
  const troops = e.units ? troopList(e.units) : [];
  if (!e.units && !out) fields.push({ name: "Troops", value: "Hidden" });
  return {
    kind: out ? "outgoing" : "incoming",
    tone,
    title: `${verb} ${e.other.name} ${fmtCoords(e.other.coords)}`,
    times: Number.isFinite(e.endMs) ? [{ label: ret ? "Back" : out ? "Lands" : e.kind === "support" ? "Arrives" : "Lands", ms: e.endMs }] : [],
    fields,
    troops,
    troopsLabel: e.units ? "Troops" : undefined,
  };
}

const who = (p: Party) => (isBarbarian(p) ? "Barbarian" : p.login || "Unknown");
const at = (p: Party) => `${p.city ? p.city + " " : ""}${p.coords ? fmtCoords(p.coords) : ""}`.trim() || "—";

/** A report (battle, scouting, support, revealed incoming…). `subject`: its cleaned subject line; `ts`: when it happened. */
export function reportCard(b: ReportBody, subject: string, ts: number): ShareCard {
  const t = reportType(b);
  const { from, to } = parties(b);
  const seal = sealOf(b);
  const fields: ShareCard["fields"] = [];
  const times: ShareCard["times"] = Number.isFinite(ts) ? [{ label: "When", ms: ts }] : [];
  let tone: ShareTone = "report";
  let troops: ShareCard["troops"] = [];
  let troopsLabel: string | undefined;

  if (t === "re_a" || t === "re_d") {
    const won = recipientWon(b);
    const attackerWon = isAttackerSide(b) ? won : won === null ? null : !won;
    fields.push({ name: "Attacker", value: `${who(from)} · ${at(from)}`, inline: true }, { name: "Defender", value: `${who(to)} · ${at(to)}`, inline: true });
    fields.push({ name: "Result", value: attackerWon === null ? SEAL_WORD[seal] : attackerWon ? "The attacker won" : "The defender held", inline: true });
    const aSent = sumUnits(b.attacking_units), aLost = sumUnits(b.dead_attacking_units), dSent = sumUnits(b.defending_units), dLost = sumUnits(b.dead_defending_units);
    fields.push({ name: "Attacker's troops", value: aSent ? `${fmtNum(aSent)} sent · ${fmtNum(aLost)} lost` : "—", inline: true });
    fields.push({ name: "Defender's troops", value: b.defending_units ? `${fmtNum(dSent)} · ${fmtNum(dLost)} lost` : "Unknown", inline: true });
    if (b.original_wall_level !== undefined) fields.push({ name: "Wall", value: num(b.resulting_wall_level ?? b.original_wall_level) !== num(b.original_wall_level) ? `${num(b.original_wall_level)} → ${num(b.resulting_wall_level)}` : String(num(b.original_wall_level)), inline: true });
    if (b.start_loyalty != null && b.end_loyalty != null) fields.push({ name: "Loyalty", value: `${num(b.start_loyalty)} → ${num(b.end_loyalty)}`, inline: true });
    const h = haulInfo(b);
    if (h && h.total) fields.push({ name: "Hauled", value: `${resourceWords(b.haul)} (${fmtNum(h.total)} of ${fmtNum(h.capacity)})` });
    if (b.spied_resources && Object.keys(b.spied_resources).length) fields.push({ name: "Resources seen", value: resourceWords(b.spied_resources) || "none" });
    troops = troopList(b.attacking_units, b.dead_attacking_units);
    troopsLabel = "Attacker (lost)";
    const def = troopList(b.defending_units, b.dead_defending_units);
    if (def.length) fields.push({ name: "Defender (lost)", value: def.map((d) => `${fmtNum(d.n ?? 0)}${d.lost ? ` (−${fmtNum(d.lost)})` : ""} ${d.name}`).join(" · ") });
    tone = isAttackerSide(b) ? "attack" : "incoming";
  } else if (t === "re_is") {
    tone = "scout";
    fields.push({ name: "Scouted", value: `${who(to)} · ${at(to)}`, inline: true }, { name: "By", value: `${who(from)} · ${at(from)}`, inline: true });
    const wall = b.spied_buildings ? num(b.spied_buildings.b_wa) : NaN;
    if (Number.isFinite(wall)) fields.push({ name: "Wall", value: String(wall), inline: true });
    if (b.spied_resources) fields.push({ name: "Resources", value: resourceWords(b.spied_resources) || "none" });
    troops = troopList(b.defending_units);
    troopsLabel = "Troops at home";
    const away = troopList(b.spied_units_out);
    if (away.length) fields.push({ name: "Troops away", value: away.map((d) => `${fmtNum(d.n ?? 0)} ${d.name}`).join(" · ") });
  } else if (t === "re_s") {
    tone = "support";
    fields.push({ name: "From", value: `${who(from)} · ${at(from)}`, inline: true }, { name: "To", value: `${who(to)} · ${at(to)}`, inline: true });
    troops = troopList(b.units, b.dead_units);
    troopsLabel = "Support (lost)";
  } else if (t === "re_i") {
    tone = "incoming";
    fields.push({ name: "From", value: `${who(from)} · ${at(from)}`, inline: true }, { name: "To", value: `${who(to)} · ${at(to)}`, inline: true });
    const land = parseGameTime(b.end_time);
    if (Number.isFinite(land)) times.unshift({ label: "Lands", ms: land });
    troops = troopList(b.incoming_units);
    troopsLabel = "Revealed troops";
  } else if (t === "re_t") {
    fields.push({ name: "From", value: `${who(from)} · ${at(from)}`, inline: true }, { name: "To", value: `${who(to)} · ${at(to)}`, inline: true });
    if (b.out_resources) fields.push({ name: "Sent", value: resourceWords(b.out_resources) || "—", inline: true });
    if (b.in_resources) fields.push({ name: "Received", value: resourceWords(b.in_resources) || "—", inline: true });
  }
  return { kind: "report", tone, title: subject || SEAL_WORD[seal], times, fields, troops, troopsLabel };
}

/** Can this report go to a Valor Tools guild's Intel (Oct 15 2026)? Scouting only, as Valor Tools reads it (live.js
 *  parseReports): an instant spy with the troops seen, or an attack that spied the city (troops away, buildings or
 *  resources) or was scouts only and saw the defenders. */
export function intelReady(b: ReportBody): boolean {
  const t = reportType(b);
  if (t === "re_is") return !!b.defending_units;
  if (t !== "re_a" || !b.defending_units) return false;
  const scoutsOnly = TROOP_KEYS.every((k) => k === "t_sc" || num(b.attacking_units?.[k]) === 0) && num(b.attacking_units?.t_sc) > 0;
  return !!(b.spied_units_out || b.spied_buildings || b.spied_resources || scoutsOnly);
}

/** Where a card goes by default: scouting to Scout reports, incoming armies to Support, everything else to Ops — or the
 *  first channel the guild has. */
export function defaultChannel(card: ShareCard, have: ShareChannel[]): ShareChannel | null {
  const want: ShareChannel[] = card.tone === "scout" ? ["intel", "ops"] : card.kind === "incoming" || card.tone === "incoming" ? ["support", "ops"] : ["ops", "intel", "support"];
  return want.find((c) => have.includes(c)) ?? have[0] ?? null;
}

// ------------------------------------------------------------------ Loyalty › Support → Valor Tools › Guild › Support
export const TOOLS_URL = "https://valor-reforged-tools.com";
/** base64url of UTF-8 JSON (Valor Tools public/guild/guild.js askFrom reads it back). */
export function b64url(o: unknown): string {
  const bytes = new TextEncoder().encode(JSON.stringify(o));
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
/** Ben, Oct 13 2026: Loyalty's Support opens the support request in Valor Tools' Guild tab (Support), filled in — the city, its
 *  coordinates, support landing by the first attack, and what's coming — for the player to pick the troops, choose whether to
 *  share it to Discord, and send. */
export function supportAskUrl(wid: number, city: { name: string; coords: [number, number] }, threats: MoveEntry[], home: [number, number] | null): string {
  const first = threats.filter((t) => Number.isFinite(t.endMs)).sort((a, b) => a.endMs - b.endMs)[0];
  const scholars = threats.filter((t) => /Scholar/.test(paceLabel(t, home))).length;
  const attack = first
    ? `${first.other.name} ${fmtCoords(first.other.coords)}${threats.length > 1 ? ` · ${threats.length} attacks` : ""}${scholars ? ` · ${scholars} at Scholar pace` : ""}`.slice(0, 120)
    : "";
  const ask = { x: city.coords[0], y: city.coords[1], city: city.name.slice(0, 40), ...(first ? { land: first.endMs } : {}), ...(attack ? { attack } : {}) };
  return `${TOOLS_URL}/app/?tab=guild&gt=support&w=${wid}&ask=${b64url(ask)}`;
}
